import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import VoucherImageUpload from "components/Common/VoucherImageUpload"
import { useMemo, useCallback, useState, useEffect } from "react"
import { toast } from "react-toastify"
import {
  Form,
  FormFeedback,
  Label,
  Button,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useQuery } from "@tanstack/react-query"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  useCreateAccountExpense,
  useEditAccountExpense,
} from "hooks/useAccountExpense"
import { useAccountExpenseTypes } from "hooks/useAccountExpenseType"
import { useAccounts } from "hooks/useAccountEntity"
import { getUsers } from "api/userApi"

const AccountExpenseAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountExpense()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountExpense()

  const [voucherImages, setVoucherImages] = useState(editedData?.images || [])

  useEffect(() => {
    setVoucherImages(editedData?.images || [])
  }, [editedData])

  const { data: typeResponse, isLoading: typeLoading } = useAccountExpenseTypes(
    { all: true, per_page: 200 },
    modal
  )

  const { data: accountResponse, isLoading: accountLoading } = useAccounts(
    { all: true, per_page: 200 },
    modal
  )

  const { data: userResponse, isLoading: userLoading } = useQuery({
    queryKey: ["users", "account-expense"],
    queryFn: () => getUsers({ all: true, per_page: 200 }),
    enabled: modal,
  })

  const typeRaw = typeResponse?.data?.data || typeResponse?.data || []
  const accountRaw = accountResponse?.data?.data || accountResponse?.data || []
  const users = userResponse?.data?.data || userResponse?.data || []

  const expenseTypeOptions = useMemo(
    () =>
      Array.isArray(typeRaw)
        ? typeRaw.map(item => ({ value: item.id, label: item.name }))
        : [],
    [typeRaw]
  )

  const accountOptions = useMemo(
    () =>
      Array.isArray(accountRaw)
        ? accountRaw.map(item => ({ value: item.id, label: item.name }))
        : [],
    [accountRaw]
  )

  const userOptions = useMemo(
    () => (Array.isArray(users) ? users.map(item => ({ value: item.id, label: item.name })) : []),
    [users]
  )

  const validationSchema = Yup.object({
    expense_type_id: Yup.mixed().required("Please select expense type"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .integer("Amount must be an integer")
      .min(1, "Amount must be at least 1")
      .required("Please enter amount"),
    account_id: Yup.mixed().required("Please select account"),
    expense_date: Yup.string().required("Please select expense date"),
    description: Yup.string().nullable(),
    approved_by: Yup.mixed().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        expense_type_id: values.expense_type_id,
        amount: Number(values.amount),
        account_id: values.account_id,
        expense_date: values.expense_date,
        description: values.description?.trim() || null,
        approved_by: values.approved_by || null,
        image_ids: voucherImages.map(img => img.id),
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Expense ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        toast.error("Failed to save expense.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, voucherImages]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      expense_type_id:
        editedData?.expense_type_id ||
        editedData?.expenseType?.id ||
        editedData?.expense_type?.id ||
        "",
      amount: editedData?.amount ?? "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      expense_date: editedData?.expense_date || "",
      description: editedData?.description || "",
      approved_by: editedData?.approved_by?.id || editedData?.approved_by || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Expense"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading || typeLoading || accountLoading || userLoading,
    [editLoading, createLoading, typeLoading, accountLoading, userLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Expense" : "Add Expense"}
      size="lg"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Expense Details</h5>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect
                  name="expense_type_id"
                  formik={formik}
                  label="Expense Type"
                  options={expenseTypeOptions}
                  placeholder="Select or type expense type"
                  loading={typeLoading}
                  required
                />
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount</Label>
                  <InputField
                    type="number"
                    name="amount"
                    id="amount"
                    min="1"
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    invalid={formik.touched.amount && !!formik.errors.amount}
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect
                  name="account_id"
                  formik={formik}
                  label="Account"
                  options={accountOptions}
                  placeholder="Select or type account"
                  loading={accountLoading}
                  required
                />
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="expense_date">Expense Date</Label>
                  <InputField
                    type="date"
                    name="expense_date"
                    id="expense_date"
                    value={formik.values.expense_date}
                    onChange={formik.handleChange}
                    invalid={formik.touched.expense_date && !!formik.errors.expense_date}
                  />
                  {formik.touched.expense_date && formik.errors.expense_date && (
                    <FormFeedback type="invalid">{formik.errors.expense_date}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Approval & Notes</h5>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect
                  name="approved_by"
                  formik={formik}
                  label="Approved By"
                  options={userOptions}
                  loading={userLoading}
                />
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="description">Description</Label>
                  <InputField
                    type="textarea"
                    name="description"
                    id="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    invalid={formik.touched.description && !!formik.errors.description}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <FormFeedback type="invalid">{formik.errors.description}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <VoucherImageUpload images={voucherImages} onChange={setVoucherImages} label="Bill / Receipt Images" />
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountExpenseAddEdit
